import type { AnswerMode } from './answers';
import { REFERENCE_DATE } from './reference';

/**
 * The chats DeepDive AI opens with, and the two things every chat needs from
 * data rather than from state: a title from a prompt, and a relative time.
 *
 * Eight seeded chats, as the Chat History frame (2059:26344) draws eight rows.
 * The frame repeats one question and one time eight times; these are eight
 * different questions the answer engine can actually answer, at times spread
 * over a fortnight, so the list reads as a history rather than a placeholder.
 *
 * Times are offsets from `REFERENCE_DATE`, the prototype's fixed "now", for the
 * same reason the corpus is: a chat "1 hour ago" that drifted to "3 days ago"
 * over a long-lived tab would be the one non-deterministic figure on the page.
 * A chat started in the session is stamped at `REFERENCE_DATE` plus its
 * sequence number and reads "Just now" — see `ChatContext`.
 */
export type SeedChat = {
  id: string;
  title: string;
  createdAt: number;
  mode: AnswerMode;
};

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

const ago = (ms: number) => REFERENCE_DATE.getTime() - ms;

export const SEED_CHATS: readonly SeedChat[] = [
  {
    id: 'sentiment-breakdown',
    title: 'What is the sentiment breakdown for my top brand?',
    createdAt: ago(1 * HOUR),
    mode: 'fast',
  },
  {
    id: 'negative-topics-last-month',
    title: 'Which topics drove the most negative mentions last month?',
    createdAt: ago(3 * HOUR),
    mode: 'expert',
  },
  {
    id: 'share-of-voice',
    title: 'How does my brand compare with its competitors on share of voice?',
    createdAt: ago(5 * HOUR),
    mode: 'fast',
  },
  {
    id: 'channels-growing',
    title: 'Which channels are growing fastest in conversation volume?',
    createdAt: ago(26 * HOUR),
    mode: 'expert',
  },
  {
    id: 'talking-about-this-week',
    title: 'What are people talking about this week?',
    createdAt: ago(2 * DAY + 4 * HOUR),
    mode: 'fast',
  },
  {
    id: 'volume-last-90-days',
    title: 'How has our mention volume trended over the last 90 days?',
    createdAt: ago(4 * DAY + 2 * HOUR),
    mode: 'fast',
  },
  {
    id: 'nps-movement',
    title: 'What is our NPS and how is it moving?',
    createdAt: ago(8 * DAY),
    mode: 'expert',
  },
  {
    id: 'engagement-sources',
    title: 'Where does most of our engagement come from?',
    createdAt: ago(15 * DAY),
    mode: 'fast',
  },
];

const seedById = new Map(SEED_CHATS.map((chat) => [chat.id, chat]));

/** The seeded title for a chat id, for the breadcrumb before state has loaded. */
export function seedChatTitle(id: string): string | undefined {
  return seedById.get(id)?.title;
}

export { chatIdFor, TITLE_MAX, titleFor } from '@/lib/chat/titles';

/** "1 hour ago", "Yesterday", "2 weeks ago" — the label the history row draws. */
export function relativeTimeLabel(
  createdAt: number,
  now: number = REFERENCE_DATE.getTime(),
): string {
  const diff = now - createdAt;
  if (diff < 60_000) return 'Just now';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(diff / HOUR);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(diff / DAY);
  if (days < 2) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (days < 30) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}
